import React, { useEffect, useState } from 'react';
import { useApp } from '@/contexts/AppContext';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Instagram, Facebook, MessageCircle, ExternalLink, RefreshCw } from 'lucide-react';

function Section({ title, icon: Icon, children, instructions }) {
  return (
    <div className="border border-border p-6" data-testid={`sec-${title}`}>
      <div className="flex items-center gap-3 mb-2">
        {Icon && <Icon size={20} strokeWidth={1.3} />}
        <h3 className="font-display text-xl">{title}</h3>
      </div>
      {instructions && <p className="text-xs font-light text-muted-foreground mb-5">{instructions}</p>}
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, testId, type = 'text' }) {
  return (
    <div>
      <Label className="overline">{label}</Label>
      <Input type={type} value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="mt-1 rounded-none border-0 border-b border-foreground focus-visible:ring-0" data-testid={testId} />
    </div>
  );
}

export default function AdminSocial() {
  const { api } = useApp();
  const [s, setS] = useState({ handles: {}, wa_recipients: [] });
  const [waText, setWaText] = useState('');
  const [log, setLog] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get('/admin/social/settings');
      setS({ handles: {}, wa_recipients: [], ...data });
      setWaText((data.wa_recipients || []).join('\n'));
      const logRes = await api.get('/admin/social/log').catch(() => ({ data: [] }));
      setLog(logRes.data || []);
    } catch (e) {
      toast.error('Could not load social settings');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);  // eslint-disable-line

  const upd = (k, v) => setS((x) => ({ ...x, [k]: v }));
  const updHandle = (k, v) => setS((x) => ({ ...x, handles: { ...(x.handles || {}), [k]: v } }));

  const save = async () => {
    const payload = { ...s, wa_recipients: waText.split('\n').map((t) => t.trim()).filter(Boolean) };
    delete payload._id; delete payload.updated_at;
    try { await api.put('/admin/social/settings', payload); toast.success('Settings saved'); load(); }
    catch (e) { toast.error('Save failed'); }
  };

  if (loading) return <div className="font-light text-muted-foreground" data-testid="social-loading">Loading…</div>;

  return (
    <div data-testid="admin-social">
      <h2 className="font-display text-2xl mb-2">Social Channels</h2>
      <p className="text-sm font-light text-muted-foreground mb-8">Paste your credentials below to enable auto-posting when admins publish new products. Footer "Follow us" links use the handle URLs.</p>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Section title="Public Handles (Footer Links)" instructions="Full URLs shown as Follow icons in the site footer.">
          <Field label="Instagram URL" value={s.handles?.ig} onChange={(v) => updHandle('ig', v)} placeholder="https://instagram.com/jjbstylish" testId="h-ig" />
          <Field label="Facebook URL" value={s.handles?.fb} onChange={(v) => updHandle('fb', v)} placeholder="https://facebook.com/jjbstylish" testId="h-fb" />
          <Field label="TikTok URL" value={s.handles?.tt} onChange={(v) => updHandle('tt', v)} placeholder="https://tiktok.com/@jjbstylish" testId="h-tt" />
          <Field label="WhatsApp URL / Channel" value={s.handles?.wa} onChange={(v) => updHandle('wa', v)} placeholder="https://wa.me/15551234567" testId="h-wa" />
        </Section>

        <Section title="Facebook" icon={Facebook} instructions="Meta Graph API. Get a long-lived Page Access Token from developers.facebook.com → your Meta App → Graph API Explorer.">
          <Field label="Facebook Page ID" value={s.fb_page_id} onChange={(v) => upd('fb_page_id', v)} placeholder="123456789012345" testId="fb-page-id" />
          <Field label="Page Access Token" value={s.fb_page_token} onChange={(v) => upd('fb_page_token', v)} placeholder="EAA…" testId="fb-token" type="password" />
        </Section>

        <Section title="Instagram" icon={Instagram} instructions="Instagram Business account linked to the Facebook Page above. Reuses the Page token if Instagram token is blank.">
          <Field label="Instagram User ID" value={s.ig_user_id} onChange={(v) => upd('ig_user_id', v)} placeholder="17841405123456789" testId="ig-user-id" />
          <Field label="Instagram Token (optional)" value={s.ig_token} onChange={(v) => upd('ig_token', v)} placeholder="leave blank to reuse FB token" testId="ig-token" type="password" />
        </Section>

        <Section title="WhatsApp Cloud API" icon={MessageCircle} instructions="WhatsApp Business via Meta Cloud API. Each recipient must opt in.">
          <Field label="Phone Number ID" value={s.wa_phone_id} onChange={(v) => upd('wa_phone_id', v)} placeholder="987654321098765" testId="wa-phone-id" />
          <Field label="Bearer Token" value={s.wa_token} onChange={(v) => upd('wa_token', v)} placeholder="EAA…" testId="wa-token" type="password" />
          <div>
            <Label className="overline">Recipients (one E.164 number per line)</Label>
            <Textarea value={waText} onChange={(e) => setWaText(e.target.value)} placeholder="+15551234567" rows={3} className="mt-1 rounded-none" data-testid="wa-recipients" />
          </div>
        </Section>

        <Section title="TikTok" icon={ExternalLink} instructions="TikTok Content Posting API. Sandbox works immediately; live posting needs app review.">
          <Field label="Access Token" value={s.tt_token} onChange={(v) => upd('tt_token', v)} placeholder="tta.…" testId="tt-token" type="password" />
        </Section>

        <Section title="Master Switch">
          <label className="flex items-center gap-3 cursor-pointer">
            <input type="checkbox" checked={s.enabled !== false} onChange={(e) => upd('enabled', e.target.checked)} data-testid="social-enabled" />
            <span className="text-sm font-light">Auto-post enabled (uncheck to disable all platforms globally)</span>
          </label>
        </Section>
      </div>

      <div className="mt-8 flex gap-3">
        <button onClick={save} className="bg-foreground text-background px-6 py-3 overline" data-testid="save-social">Save Settings</button>
      </div>

      <div className="mt-16">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-xl">Recent Auto-Post Log</h3>
          <button onClick={load} className="overline link-underline inline-flex gap-2 items-center"><RefreshCw size={12} /> Refresh</button>
        </div>
        {log.length === 0 ? <p className="text-sm font-light text-muted-foreground" data-testid="log-empty">No posts yet. Create a product with "Share to social" checked.</p> : (
          <div className="space-y-2">
            {log.map((row) => (
              <div key={row.id} className="border border-border p-4 text-xs font-light" data-testid={`log-${row.id}`}>
                <div className="overline">{new Date(row.created_at).toLocaleString()} · Product {row.product_id?.slice(0,8)}</div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-2">
                  {['facebook','instagram','whatsapp','tiktok'].map((p) => (
                    <div key={p} className={row[p]?.ok ? 'text-accent' : 'text-muted-foreground'}>
                      <span className="overline">{p}</span>: {row[p]?.ok ? 'posted' : (row[p]?.skipped || 'failed')}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
